import { Alert, Container, Grid, Typography, useMediaQuery } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { DocumentData } from 'firebase/firestore';
import React, { useEffect, useState } from 'react';
import { Outlet, useParams } from 'react-router';

import LessonCard from 'components/dashboard/LessonCard/LessonCard';
import LessonCardSkeleton from 'components/dashboard/LessonCard/LessonCardSkeleton';
import HeaderBreadcrumbs from 'components/HeaderBreadcrumbs/HeaderBreadcrumbs';
import Page from 'components/Page/Page';
import SkeletonWrapper from 'components/SkeletonWrapper/SkeletonWrapper';
import { getGroupModuleLastLessonNumber, getModule, getModuleLessons } from 'external/firebase/controllers/db';
import useAuth from 'hooks/useAuth';
import useLocales from 'hooks/useLocales';
import { PATH_DASHBOARD } from 'routes/paths';
import { APP_ROLES, ProfileGroup, getManagedGroupIds, hasAnyRole } from 'utils/roles';

const STAFF_ROLES = [APP_ROLES.SUPER_ADMIN, APP_ROLES.ADMIN, APP_ROLES.DESTYTOJAS, APP_ROLES.KURATORIUS];

export const StyledGrid = styled(Grid)(({ theme }) => ({
    display: 'grid',
    alignItems: 'center',
    gap: '20px',
    gridTemplateColumns: '1fr',
    [theme.breakpoints.up('md')]: {
        gridTemplateColumns: '1fr 1fr',
    },
}));

const ModuleNew: React.FunctionComponent = () => {
    const { profile } = useAuth();
    const { module: moduleId, course: courseId } = useParams();
    const { translate } = useLocales();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [module, setModule] = useState<DocumentData>({});
    const [isPublicModule, setIsPublicModule] = useState(false);
    const [lessons, setLessons] = useState<DocumentData>([]);
    const [error, setError] = useState<string | undefined>();
    const [isLoading, setIsLoading] = useState(true);

    const fetchModule = async (module: string) => {
        const resModule = await getModule(module);
        if (resModule?.ok !== false) {
            const data = resModule as DocumentData;
            setModule(data);
            setIsPublicModule(data?.public === true);
        }
    };

    const fetchModuleLessons = async (groups: ProfileGroup, module: string) => {
        try {
            let lessonNumbers: number | number[];
            if (hasAnyRole(groups, STAFF_ROLES)) {
                lessonNumbers = 99;
            } else {
                const classGroup = getManagedGroupIds(groups)[0];
                if (!classGroup) {
                    setLessons([]);
                    setError(undefined);
                    return;
                }
                lessonNumbers = await getGroupModuleLastLessonNumber(classGroup, module);
            }
            const lessons = await getModuleLessons(module, lessonNumbers);
            setLessons(lessons);
            setError(undefined);
        } catch (error) {
            console.error(error);
            setError(translate('error_fetching_lessons'));
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (moduleId) {
            fetchModule(moduleId);
        }
    }, [moduleId]);

    useEffect(() => {
        if (!moduleId) return;
        if (profile?.group) {
            fetchModuleLessons(profile.group, moduleId);
        } else if (isPublicModule) {
            const fetchPublicLessons = async () => {
                try {
                    const lessons = await getModuleLessons(moduleId, 99);
                    setLessons(lessons);
                    setError(undefined);
                } catch (error) {
                    console.error(error);
                    setError(translate('error_fetching_lessons'));
                } finally {
                    setIsLoading(false);
                }
            };
            fetchPublicLessons();
        }
    }, [profile?.group, moduleId, isPublicModule]);

    return (
        <Page title={`${translate('lessons')} | CodeAcademy Online`}>
            <Container maxWidth='lg'>
                <HeaderBreadcrumbs
                    heading={module?.title}
                    links={[
                        { name: translate('modules'), href: `${PATH_DASHBOARD.courses.root}/${courseId}` },
                        { name: module?.title, href: '' },
                    ]}
                />

                {isLoading && (
                    <Grid container spacing={3} justifyContent='center'>
                        <SkeletonWrapper copies={5}>
                            <Grid item xs={12}>
                                <LessonCardSkeleton />
                            </Grid>
                        </SkeletonWrapper>
                    </Grid>
                )}

                {error && (
                    <Alert severity='error'>
                        {translate('error_fetching_lessons')} {translate('please_try_again_or_contact_admin')}
                    </Alert>
                )}

                {!isLoading && lessons.length > 4 && (
                    <>
                        <Grid
                            container
                            spacing={3}
                            justifyContent='center'
                            style={
                                !isMobile
                                    ? {
                                          maxWidth: '50%',
                                      }
                                    : {}
                            }
                        >
                            <Grid item xs={12} key={'latest-lesson'}>
                                <Typography variant='h6'>{translate('lastest_lesson')}</Typography>
                            </Grid>
                            <Grid item xs={12} key={lessons[lessons.length - 1].id}>
                                <LessonCard lesson={lessons[lessons.length - 1]} order={lessons.length} />
                            </Grid>
                            <Grid item xs={12} key={'all-lessons'}>
                                <Typography variant='h6' my={3}>
                                    {translate('all_lessons')}
                                </Typography>
                            </Grid>
                        </Grid>
                    </>
                )}

                {!isLoading &&
                    (lessons.length > 0 ? (
                        <>
                            <StyledGrid container spacing={3} justifyContent='center'>
                                {lessons.map((lesson, i) => (
                                    <Grid item xs={12} key={lesson.id} style={{ height: '100%' }}>
                                        <LessonCard lesson={lesson} order={i + 1} />
                                    </Grid>
                                ))}
                            </StyledGrid>
                        </>
                    ) : (
                        <Grid container spacing={3} justifyContent='center'>
                            <Typography variant='h6' sx={{ px: 5, mt: 10, mb: 5 }}>
                                {translate('no_lessons_found')}
                            </Typography>
                        </Grid>
                    ))}
                <Outlet />
            </Container>
        </Page>
    );
};

export default ModuleNew;
